"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/Button";
import { ErrorCard } from "@/components/ErrorCard";
import { recoverFromChunkError } from "@/lib/chunkRecovery";
import { reportClientError } from "@/lib/telemetry";

export default function ReaderSegmentError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  // A chunk that vanished under a mid-visit deploy is repairable, and the
  // card below can't repair it — reload instead. Decided during the first
  // render (via the lazy initializer) so the crash screen never flashes up
  // on its way out; the sessionStorage guard inside makes the repeat call
  // under StrictMode's double render a no-op.
  const [recovering] = useState(() => recoverFromChunkError(error));

  useEffect(() => {
    console.error(error);
    reportClientError(error, "reader", { recovered: String(recovering) });
  }, [error, recovering]);

  // The reload is already scheduled; showing anything would only flicker.
  if (recovering) return null;

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-[640px] items-center px-[var(--gutter)]">
      <div className="w-full">
        <ErrorCard
          title="The book wouldn't open"
          body="Something went wrong loading this book. Try again, or head back to your library."
          digest={error.digest}
          action={
            <>
              <Button onClick={() => unstable_retry()}>Try again</Button>
              <Button variant="secondary" href="/library">
                My Library
              </Button>
            </>
          }
        />
      </div>
    </div>
  );
}
